import React from 'react';
import PropTypes from 'prop-types';
import Button from '../Button/Button';
import { CommonValidation } from '../CommonValidation/commonValidation';

const ValidationTriggerType = {
  TriggerFieldChange: 1,
  TriggerFieldExit: 2,
  TriggerManual: 3
};

/** Responsible for having an input field with a button styled nicely next to it. This can be used for fields where input is required along with a search afterwards. This component uses the "Button" component and requires it's props to be passed down to the button*/
export default class TextButtonInput extends React.Component {
  static isValidValue(value) {
    var isValid = !(value == null || value == '' || value.length == 0);
    return isValid;
  }

  componentDidUpdate(prevProps) {
    this.handleComponentDidUpdate(prevProps, this.props);
  }

  handleComponentDidUpdate(prevProps) {
    var validateManualTrigger =
      this.props.validateManual && !prevProps.validateManual;
    if (validateManualTrigger) {
      let textValue = this.props.value;
      this.handleValidate(textValue, ValidationTriggerType.TriggerManual);
    }
  }

  onChange = (e) => {
    let value = e.target.value;
    this.handleValidate(value, ValidationTriggerType.TriggerFieldChange);

    this.callOnChange(e);
  };

  // add a handler to trim the values on leave ...
  onLeave = (e) => {
    let value = e.target.value;

    this.handleValidate(value, ValidationTriggerType.TriggerFieldExit);

    if (value != undefined) {
      value = value.trim();
      e.target.value = value;

      this.onChange(e);
    }

    if (typeof this.props.onLeave == 'function') {
      const key = this.props.datakey || '';
      this.props.onLeave(e, key);
    }
  };

  onClick = (e) => {
    if (this.props.stopPropagationOnClick) {
      e.stopPropagation();
    }
  };

  callOnChange = (e) => {
    const key = this.props.datakey || '';
    if (typeof this.props.onChange == 'function') this.props.onChange(e, key);
  };

  onKeyPress = (e) => {
    if (this.props.handleReturn) {
      if (e.key == 'Enter') {
        var blurEvent = { target: { value: null } };
        this.onLeave(blurEvent);
      }
    }

    if (typeof this.props.onKeyPress == 'function') this.props.onKeyPress(e);
  };

  handleValidate = (value, triggerType) => {
    let errors = [];

    if (this.props.validateRequired == true) {
      if (triggerType == ValidationTriggerType.TriggerFieldChange) {
        const controlid = this.props.validateKey || this.props.name;
        const isRequiredValid = !CommonValidation.isEmpty(value);

        errors.push({
          id: controlid,
          text: `${this.props.label} is required!`,
          valid: isRequiredValid
        });
      }
    }

    if (errors.length > 0)
      if (this.props.handleValidate) this.props.handleValidate(errors);
  };

  getControlValue = () => {
    const { datakey, value } = this.props;

    let controlValue = null;
    if (datakey != null) controlValue = value[datakey];
    else controlValue = value;

    return controlValue;
  };

  render() {
    var wrapperClass = 'form-group';
    if (this.props.error && this.props.error.length > 0)
      wrapperClass += ' has-error';

    var labelHtml = '';
    if (this.props.hasLabel && this.props.hasLabel == true)
      labelHtml = <label htmlFor={this.props.name}>{this.props.label}:</label>;

    const controlValue = this.getControlValue();

    var inputControl = (
      <input
        id={this.props.name}
        type='text'
        autoComplete='off'
        className='form-control'
        placeholder={this.props.placeholder || this.props.label}
        autoFocus={this.props.autoFocus}
        name={this.props.name}
        value={controlValue}
        onKeyPress={this.onKeyPress}
        onChange={this.onChange}
        onFocus={this.props.onFocus}
        onBlur={this.onLeave}
        onClick={this.onClick}
      />
    );

    const readOnly = this.props.readonly == true;
    if (readOnly) {
      inputControl = (
        <input
          id={this.props.name}
          type='text'
          autoComplete='off'
          className='form-control'
          placeholder={this.props.placeholder || this.props.label}
          name={this.props.name}
          value={controlValue}
          readOnly
        />
      );
    }

    let classes = 'field';
    if (this.props.className) classes += ' ' + this.props.className;

    return (
      <div className={wrapperClass}>
        {labelHtml}
        <div className={classes}>
          <div className='input-group'>
            {inputControl}
            <Button
              name={`${this.props.name}-btn`}
              className={`btn btn-primary base-level-index ${this.props.buttonStyle}`}
              disabled={this.props.buttonDisabled}
              onClick={this.props.onButtonClick}
              text={this.props.buttonText}
              waitText={this.props.waitText}
              waitValue={this.props.waitValue}
              waitOnClick={this.props.waitOnClick}
            />
          </div>
        </div>
        <div className='input'>{this.props.error}</div>
      </div>
    );
  }
}

TextButtonInput.propTypes = {
  /** An error message to display */
  error: PropTypes.string,
  /** Additional styles to wrap */
  className: PropTypes.string,
  /** The name reference */
  name: PropTypes.string,
  /** The placeholder value for the input */
  placeHolder: PropTypes.string,
  /** The label that appears above the input */
  label: PropTypes.string,
  /** The current value */
  value: PropTypes.string.isRequired,
  /** An index of what piece of data is being modified */
  datakey: PropTypes.string,
  /** Decides if the input is editable */
  readonly: PropTypes.bool,
  /** Decides a label is shown */
  hasLabel: PropTypes.bool,
  /** Decides if the input is focused automatically on page load */
  autoFocus: PropTypes.bool,
  /** Allows enter to be pressed to blur out of the input (will not trigger the button)*/
  handleReturn: PropTypes.bool,
  /** Stops propagation on the event that is trigged*/
  stopPropagationOnClick: PropTypes.bool,
  /** A function to specify what should happen when the input is focused */
  onFocus: PropTypes.func,
  /** A function that can be trigged when a key is pressed, can also handle bluring on enter if handleReturn is true */
  onKeyPress: PropTypes.func,
  /** A function to be called on every key stroke to pass the value back up to it's controller */
  onChange: PropTypes.func.isRequired,
  /** A function to be called when bluring out of the input */
  onLeave: PropTypes.func,
  /** Passed down to Button component - If the button is disabled and can't be clicked */
  buttonDisabled: PropTypes.bool,
  /** Passed down to Button component - A function that registers what the click of the button does */
  onButtonClick: PropTypes.func.isRequired,
  /** Passed down to Button component - If the button is currently in waiting mode or not */
  waitValue: PropTypes.bool,
  /** Passed down to Button component - Decides if the button should have a timer when clicked*/
  waitOnClick: PropTypes.bool,
  /** Passed down to Button component - Any extra styling required for the button*/
  buttonStyle: PropTypes.string,
  /** Passed down to Button component - The text on the button */
  buttonText: PropTypes.string,
  /** Passed down to Button component - The text to be displayed when the button is waiting after being clicked */
  waitText: PropTypes.string
};
